import type { Cohort, Quiz } from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import { addOne } from '@efdigital/ll-admin-v2/helpers/add-one.ts';
import type { ParamsProgramId } from '@efdigital/ll-admin-v2/routes/programs/program';
import type { ParamsCohortId } from '@efdigital/ll-admin-v2/routes/programs/program/cohort';
import { array } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { set } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { DragToSort } from '../drag-to-sort.gts';
export class EditCohortCertificates extends Component<
  EditRecordSignature<Cohort, { params: ParamsProgramId & ParamsCohortId }>
> {
  @service declare store: Store;

  setQuizValue = (property: string, quiz: Quiz, event: Event) => {
    const target = event.target as HTMLInputElement;
    set(quiz, property, target.value);
  };

  removeQuizAt = (quizzes: Quiz[], index: number) => {
    const removed = quizzes[index];
    if (removed) {
      // Immediate rollback before removing from relationship
      removed.rollbackAttributes();
    }
    quizzes.splice(index, 1);
  };

  newQuiz = async () => {
    const quiz = this.store.createRecord<Quiz>(DataTypeQuiz.Quiz, {
      title: 'New Certificate / Survey Quiz',
      language: 'en',
      category1: 'Surveys',
      category2: 'Custom',
      category3: '',
      category4: '',
      category5: '',
      markdown: '',
      markdownOutput: '',
      markdownReadOnly: '',
      css: '',
      templateInput: 'default',
      templateOutput: 'default',
      templatePdf: 'default',
      sub: 0,
      totals: [],
      categories: [],
      comparisons: [],
      systemLineage: 'll-admin-v2',
    });

    const quizzes = await this.args.item.certificates;
    quizzes.push(quiz);
  };

  <template>
    <EditRecord
      @editLabel="Manage Certificates And Surveys"
      @item={{@item}}
      @context={{@context}}
      @maxDepth={{2}}
      ...attributes
    >
      <:form as |uniqueId isDisabled isReadOnly|>
        <fieldset
          disabled={{isDisabled}}
          class="mb-0"
          id="{{uniqueId}}-certificates"
        >
          <legend class="gud-eyebrow">Certificate / Survey Quizzes</legend>

          <p class="form-text">Certificates are used to award participants for
            completing the program, while surveys are used to gather feedback
            from participants. These are not used to measure the participant's
            understanding of the content, but rather to gather feedback and
            improve the program.</p>

          {{#if isReadOnly}}
            <Await @promise={{@item.certificates}}>
              <:success as |certificates|>
                {{#if certificates.length}}
                  <div class="list-group mb-3">
                    {{#each certificates as |certificate|}}
                      <LinkTo
                        @route="programs.program.cohort.content.certificate"
                        @models={{array
                          @params.program_id
                          @params.cohort_id
                          certificate.id
                        }}
                        class="list-group-item"
                      >{{certificate.title}}
                        <iconify-icon inline icon="ph:arrow-right" /></LinkTo>
                    {{/each}}
                  </div>
                {{else}}
                  <p>No certificate / survey quizzes.</p>
                {{/if}}
              </:success>
            </Await>
          {{else}}
            <Await @promise={{@item.certificates}}>
              <:success as |certificates|>
                <div class="list-group mb-3 gap-1">
                  <DragToSort @items={{certificates}}>
                    <:item as |dropZone draggable certificate index|>
                      <div class="input-group" {{dropZone index}}>
                        <span
                          class="input-group-text justify-content-center fw-bold drag-handle w-3rem"
                          {{draggable certificate index}}
                          title="Drag to reorder"
                        >
                          {{addOne index}}
                        </span>
                        <input
                          type="text"
                          class="form-control list-group-item"
                          value={{certificate.title}}
                          aria-label="Certificate or survey title"
                          required
                          {{debounce
                            (fn this.setQuizValue "title" certificate)
                          }}
                        />
                        <button
                          aria-label="Remove certificate or survey"
                          class="btn btn-outline-primary rounded-end-1"
                          type="button"
                          {{on
                            "click"
                            (fn this.removeQuizAt certificates index)
                          }}
                        ><iconify-icon inline icon="ph:trash-bold" /></button>
                      </div>
                    </:item>
                  </DragToSort>
                </div>
              </:success>
            </Await>
          {{/if}}
        </fieldset>
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            id="{{uniqueId}}-newCertificate"
            {{on "click" this.newQuiz}}
          >Add Certificate / Survey</button>
        {{/unless}}
      </:buttons>
    </EditRecord>
  </template>
}
